import {
  Flag,
  CheckCircle,
  Award,
  Crown,
  Rocket,
  DollarSign,
  Coins,
  PiggyBank,
  Landmark,
  Trophy,
  Flame,
  Zap,
  Medal,
  Star,
  ShieldCheck,
  Swords,
  Home,
  Clock,
  Compass,
  Link,
  ListChecks,
  Lock,
  type LucideIcon,
} from 'lucide-react'
import type { Achievement } from '@/types'
import { ACHIEVEMENT_CATEGORY_COLORS } from '@/types/gamification.types'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Progress } from '@/components/ui/progress'

const iconMap: Record<string, LucideIcon> = {
  Flag,
  CheckCircle,
  Award,
  Crown,
  Rocket,
  DollarSign,
  Coins,
  PiggyBank,
  Landmark,
  Trophy,
  Flame,
  Zap,
  Medal,
  Star,
  ShieldCheck,
  Swords,
  Home,
  Clock,
  Compass,
  Link,
  ListChecks,
}

interface AchievementBadgeProps {
  achievement: Achievement
  size?: 'sm' | 'md' | 'lg'
  showProgress?: boolean
  showTooltip?: boolean
}

export function AchievementBadge({
  achievement,
  size = 'md',
  showProgress = true,
  showTooltip = true,
}: AchievementBadgeProps) {
  const Icon = iconMap[achievement.icon] || Award
  const isUnlocked = !!achievement.unlockedAt
  const categoryColors = ACHIEVEMENT_CATEGORY_COLORS[achievement.category]
  
  const sizeClasses = {
    sm: 'w-12 h-12',
    md: 'w-16 h-16',
    lg: 'w-20 h-20',
  }
  
  const iconSizes = {
    sm: 'h-5 w-5',
    md: 'h-7 w-7',
    lg: 'h-9 w-9',
  }

  const progressPercent = achievement.requirement > 0
    ? (achievement.progress / achievement.requirement) * 100
    : 0

  const badge = (
    <div className="flex flex-col items-center gap-2">
      <div
        className={cn(
          'relative rounded-full flex items-center justify-center transition-all',
          sizeClasses[size],
          isUnlocked
            ? cn('border-2', categoryColors)
            : 'bg-muted border-2 border-muted-foreground/20'
        )}
      >
        {isUnlocked ? (
          <Icon className={cn(iconSizes[size], categoryColors.split(' ')[0])} />
        ) : (
          <Lock className={cn(iconSizes[size], 'text-muted-foreground/50')} />
        )}
        
        {/* Points indicator */}
        {isUnlocked && (
          <div className="absolute -bottom-1 -right-1 bg-primary text-primary-foreground text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {achievement.points}
          </div>
        )}
      </div>
      
      {/* Name */}
      <span className={cn(
        'text-xs font-medium text-center max-w-[80px] leading-tight',
        !isUnlocked && 'text-muted-foreground'
      )}>
        {achievement.name}
      </span>
      
      {/* Progress bar for locked achievements */}
      {!isUnlocked && showProgress && achievement.requirement > 1 && (
        <div className="w-full max-w-[60px]">
          <Progress value={progressPercent} className="h-1" />
          <span className="text-[10px] text-muted-foreground">
            {achievement.progress}/{achievement.requirement}
          </span>
        </div>
      )}
    </div>
  )

  if (!showTooltip) return badge

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-pointer">{badge}</div>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[200px]">
        <div className="space-y-1">
          <p className="font-semibold">{achievement.name}</p>
          <p className="text-sm text-muted-foreground">{achievement.description}</p>
          {isUnlocked ? (
            <p className="text-xs text-success">
              Unlocked {new Date(achievement.unlockedAt!).toLocaleDateString()}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Progress: {achievement.progress}/{achievement.requirement}
            </p>
          )}
          <p className="text-xs font-medium">{achievement.points} XP</p>
        </div>
      </TooltipContent>
    </Tooltip>
  )
}
